import React, { useState, useEffect } from 'react';
import { 
  Upload, 
  FileImage, 
  Eye, 
  MessageSquare, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle,
  Download,
  Trash2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const LabelUploadArea = () => {
  const { user, authToken } = useAuth();
  const { toast } = useToast();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingOrderId, setUploadingOrderId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadNotes, setUploadNotes] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewMockup, setReviewMockup] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (user) {
      loadOrdersWithLabels();
    }
  }, [user]);

  const loadOrdersWithLabels = async () => {
    try {
      setLoading(true);
      
      // Get token from context or localStorage as fallback
      let token = authToken;
      if (!token) {
        token = localStorage.getItem('authToken');
      }
      
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${backendUrl}/api/labels/customer/my-orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      } else {
        throw new Error('Errore nel caricamento degli ordini');
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare gli ordini",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event, orderId) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (10KB - 50MB)
      if (file.size < 10 * 1024) {
        toast({
          title: "File troppo piccolo",
          description: "Il file deve essere almeno 10KB",
          variant: "destructive"
        });
        return;
      }
      
      if (file.size > 50 * 1024 * 1024) {
        toast({
          title: "File troppo grande",
          description: "Il file non può superare 50MB",
          variant: "destructive"
        });
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Formato non supportato",
          description: "Usa JPG, PNG o PDF",
          variant: "destructive"
        });
        return;
      }

      setSelectedFile(file);
      setSelectedOrder(orderId);
    }
  };

  const uploadLabelFile = async () => {
    if (!selectedFile || !selectedOrder) return;

    try {
      setUploadingOrderId(selectedOrder);

      // Get token from context or localStorage as fallback
      let token = authToken;
      if (!token) {
        token = localStorage.getItem('authToken');
      }
      
      if (!token) {
        toast({
          title: "Errore di autenticazione",
          description: "Token di accesso non trovato. Riprova ad effettuare il login.",
          variant: "destructive",
        });
        return;
      }

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('order_id', selectedOrder);
      if (uploadNotes.trim()) {
        formData.append('notes', uploadNotes.trim());
      }

      const response = await fetch(`${backendUrl}/api/labels/customer/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: "Upload completato",
          description: "Etichetta caricata con successo",
        });

        // Reset form
        setSelectedFile(null);
        setSelectedOrder(null);
        setUploadNotes('');
        
        // Reload orders
        await loadOrdersWithLabels();
      } else {
        throw new Error(result.message || 'Errore durante l\'upload');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Errore upload",
        description: error.message || 'Errore durante l\'upload del file',
        variant: "destructive"
      });
    } finally {
      setUploadingOrderId(null);
    }
  };

  const handleReviewSubmission = async (status) => {
    if (!reviewMockup) return;

    try {
      // Get token from context or localStorage as fallback
      let token = authToken;
      if (!token) {
        token = localStorage.getItem('authToken');
      }
      
      if (!token) {
        toast({
          title: "Errore di autenticazione",
          description: "Token di accesso non trovato. Riprova ad effettuare il login.",
          variant: "destructive",
        });
        return;
      }

      const reviewData = {
        mockup_file_id: reviewMockup.id,
        status: status,
        customer_comments: reviewComment.trim() || null
      };

      const response = await fetch(`${backendUrl}/api/labels/customer/review`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reviewData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: "Risposta inviata",
          description: "La tua risposta è stata inviata con successo",
        });

        setShowReviewModal(false);
        setReviewComment('');
        setReviewMockup(null);
        
        // Reload orders
        await loadOrdersWithLabels();
      } else {
        throw new Error(result.message || 'Errore nell\'invio della risposta');
      }
    } catch (error) {
      console.error('Review submission error:', error);
      toast({
        title: "Errore",
        description: error.message || 'Errore nell\'invio della risposta',
        variant: "destructive"
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'in_review':
        return <Eye className="w-5 h-5 text-blue-500" />;
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'revision_requested':
        return <MessageSquare className="w-5 h-5 text-orange-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'In attesa di revisione';
      case 'in_review':
        return 'Bozza disponibile per revisione';
      case 'approved':
        return 'Approvata - In produzione';
      case 'revision_requested':
        return 'Richieste modifiche';
      case 'rejected':
        return 'Rifiutata';
      default:
        return 'Stato sconosciuto';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-yellow-800 mb-2">Autenticazione Richiesta</h3>
          <p className="text-yellow-600">
            Devi essere autenticato per accedere all'Area invio file.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">📁 Area Invio File</h2>
        <p className="text-gray-600 mb-6">
          Carica i file delle tue etichette personalizzate, visualizza le bozze create dal nostro team e approva o richiedi modifiche.
        </p>

        {orders.length === 0 ? (
          <div className="text-center py-12">
            <FileImage className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun ordine con etichette personalizzate</h3>
            <p className="text-gray-500">
              Gli ordini con design professionale o "carico dopo" appariranno qui.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((orderData) => {
              const order = orderData.order;
              const workflow = orderData.workflow;
              
              return (
                <div key={order.id} className={`border rounded-lg p-6 ${
                  workflow?.customer_files && workflow.customer_files.length > 0 
                    ? 'border-green-300 bg-green-50' 
                    : 'border-gray-200'
                }`}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Ordine #{order.orderNumber || order.id}
                        </h3>
                        {workflow?.customer_files && workflow.customer_files.length > 0 && (
                          <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                            ✓ File Caricato
                          </span>
                        )}
                        {workflow?.admin_mockups && workflow.admin_mockups.length > 0 && (
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                            🎨 Bozza Ricevuta
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {new Date(order.createdAt).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(workflow?.current_status || 'pending')}
                      <span className="text-sm font-medium">
                        {getStatusText(workflow?.current_status || 'pending')}
                      </span>
                    </div>
                  </div>

                  {/* Customer Files */}
                  {workflow?.customer_files && workflow.customer_files.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">File caricati:</h4>
                      <div className="space-y-2">
                        {workflow.customer_files.map((file) => (
                          <div key={file.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <FileImage className="w-5 h-5 text-blue-500" />
                              <div>
                                <p className="text-sm font-medium">{file.original_filename}</p>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(file.file_size)} • {new Date(file.uploaded_at).toLocaleDateString('it-IT')}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => window.open(`${backendUrl}/api/upload/serve/${file.file_path}`, '_blank')}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Visualizza
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Admin Mockups */}
                  {workflow?.admin_mockups && workflow.admin_mockups.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Bozze dal team:</h4>
                      <div className="space-y-2">
                        {workflow.admin_mockups.map((mockup) => (
                          <div key={mockup.id} className="flex items-center justify-between bg-amber-50 p-3 rounded-lg border border-amber-200">
                            <div className="flex items-center space-x-3">
                              <Download className="w-5 h-5 text-amber-600" />
                              <div>
                                <p className="text-sm font-medium">{mockup.original_filename}</p>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(mockup.file_size)} • {new Date(mockup.uploaded_at).toLocaleDateString('it-IT')}
                                </p>
                                {mockup.notes && (
                                  <p className="text-xs text-gray-600 mt-1">Note: {mockup.notes}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => window.open(`${backendUrl}/api/upload/serve/${mockup.file_path}`, '_blank')}
                                className="text-amber-600 hover:text-amber-800 text-sm font-medium"
                              >
                                Visualizza
                              </button>
                              {workflow.current_status === 'in_review' && (
                                <button
                                  onClick={() => {
                                    setReviewMockup(mockup);
                                    setShowReviewModal(true);
                                  }}
                                  className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                                >
                                  Rispondi
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upload Area */}
                  {(order.status === 'waiting_label' || order.status === 'payment_confirmed' || order.requiresLabelUpload) && 
                   (!workflow?.customer_files || workflow.customer_files.length === 0) && (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-4" />
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Carica il file dell'etichetta
                        </h4>
                        <p className="text-xs text-gray-500 mb-4">
                          JPG, PNG, PDF • 10KB - 50MB
                        </p>
                        
                        <input
                          type="file"
                          id={`file-${order.id}`}
                          className="hidden"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(e) => handleFileSelect(e, order.id)}
                          disabled={uploadingOrderId === order.id}
                        />
                        
                        <label
                          htmlFor={`file-${order.id}`}
                          className={`cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                            uploadingOrderId === order.id
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-purple-600 hover:bg-purple-700'
                          }`}
                        >
                          {uploadingOrderId === order.id ? 'Caricamento...' : 'Seleziona File'}
                        </label>
                      </div>
                      
                      {selectedOrder === order.id && selectedFile && (
                        <div className="mt-4 bg-white p-4 rounded border">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <FileImage className="w-5 h-5 text-blue-500" />
                              <span className="text-sm font-medium">{selectedFile.name}</span>
                              <span className="text-xs text-gray-500">
                                ({formatFileSize(selectedFile.size)})
                              </span>
                            </div>
                            <button
                              onClick={() => {
                                setSelectedFile(null);
                                setSelectedOrder(null);
                                setUploadNotes('');
                              }}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          
                          <textarea
                            placeholder="Note aggiuntive (opzionale)"
                            value={uploadNotes}
                            onChange={(e) => setUploadNotes(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-sm mb-3"
                            rows="2"
                          />
                          
                          <button
                            onClick={uploadLabelFile}
                            disabled={uploadingOrderId === order.id}
                            className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700 disabled:bg-gray-400 text-sm font-medium"
                          >
                            {uploadingOrderId === order.id ? (
                              <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Caricamento...
                              </div>
                            ) : (
                              'Carica Etichetta'
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && reviewMockup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Revisiona Bozza Etichetta
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">File: {reviewMockup.original_filename}</p>
              <button
                onClick={() => window.open(`${backendUrl}/api/upload/serve/${reviewMockup.file_path}`, '_blank')}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Visualizza bozza →
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Commenti (opzionale)
              </label>
              <textarea
                placeholder="Inserisci eventuali commenti o richieste di modifica..."
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg"
                rows="3"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => handleReviewSubmission('approved')}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700"
              >
                ✓ Approva
              </button>
              <button
                onClick={() => handleReviewSubmission('revision_requested')}
                className="flex-1 bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700"
              >
                🔄 Richiedi Modifiche
              </button>
            </div>

            <button
              onClick={() => {
                setShowReviewModal(false);
                setReviewComment('');
                setReviewMockup(null);
              }}
              className="w-full mt-3 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabelUploadArea;